import { useEffect, useId, useRef } from 'react';

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: HTMLElement,
        options: {
          sitekey: string;
          callback: (token: string) => void;
          'error-callback'?: () => void;
          'expired-callback'?: () => void;
          theme?: 'light' | 'dark' | 'auto';
        },
      ) => string;
      reset: (widgetId?: string) => void;
      remove: (widgetId?: string) => void;
    };
  }
}

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
let scriptLoadPromise: Promise<void> | null = null;

function loadTurnstileScript(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  if (scriptLoadPromise) return scriptLoadPromise;
  scriptLoadPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () =>
      reject(new Error(`Failed to load Cloudflare Turnstile script from ${SCRIPT_SRC}`));
    document.head.appendChild(script);
  });
  return scriptLoadPromise;
}

/**
 * Bot protection on the checkout form (MASTER_PROMPT.md requirement). `siteKey` must be passed in
 * from runtime config (src/theme/types.ts CheckoutRuntimeConfig.turnstileSiteKey) - never
 * hardcoded, since each tenant/domain needs its own Turnstile site key registered against that
 * domain in the Cloudflare dashboard.
 */
export function Turnstile({
  siteKey,
  onToken,
  onExpire,
}: {
  siteKey: string;
  onToken: (token: string) => void;
  onExpire?: () => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | undefined>(undefined);
  const labelId = useId();

  useEffect(() => {
    let cancelled = false;
    loadTurnstileScript()
      .then(() => {
        if (cancelled || !containerRef.current || !window.turnstile) return;
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          callback: onToken,
          'error-callback': onExpire,
          'expired-callback': onExpire,
          theme: 'light',
        });
      })
      .catch(() => {
        // Surfaced to the buyer via the checkout form's own error banner, not here - this widget
        // has no error UI of its own, it just never calls onToken, which blocks submission.
      });
    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
      }
    };
  }, [siteKey, onToken, onExpire]);

  return (
    <div>
      <span id={labelId} className="sr-only">
        Bot verification
      </span>
      <div ref={containerRef} aria-labelledby={labelId} />
    </div>
  );
}
